import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useParams, useLocation, useNavigate } from 'react-router-dom';
import { LanguageIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import Logo from '../assets/logo.svg';

export default function Header() {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = lang || i18n.language || 'et';

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleEscape = (e) => { if (e.key === 'Escape') setIsMobileMenuOpen(false); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileMenuOpen]);

  const toggleLang = async () => {
    const targetLang = currentLang === 'et' ? 'en' : 'et';
    const path = location.pathname;

    // Blog posts use different slugs per language, so a plain prefix swap would
    // land on the wrong (same-slug) post. Map to the translated post via altSlug;
    // if the post has no translation, fall back to the target-language blog index.
    // blogLoader is imported lazily here so its eager markdown glob stays out of
    // the eager Header/main bundle and only loads when actually toggling on a post.
    const blogMatch = path.match(/^\/(en|et)\/blog\/([^/]+)\/?$/);
    if (blogMatch) {
      const { getPostBySlug } = await import('../utils/blogLoader');
      const post = getPostBySlug(blogMatch[2]);
      navigate(post?.altSlug ? `/${targetLang}/blog/${post.altSlug}` : `/${targetLang}/blog`);
      return;
    }

    let newPath = path.replace(`/${currentLang}`, `/${targetLang}`);
    if (newPath === path) {
      newPath = `/${targetLang}`;
    }
    navigate(newPath);
  };

  const navLinkClasses = ({ isActive }) => 
    `relative py-1 transition-colors duration-200 ${
      isActive 
        ? "text-blue-400 md:after:absolute md:after:bottom-0 md:after:left-0 md:after:w-full md:after:h-0.5 md:after:bg-blue-400"
        : "text-gray-300 hover:text-blue-400"
    }`;

  const navigation = [
    { name: t('nav.home'), path: '' }, 
    { name: t('nav.services'), path: 'services' },
    { name: t('nav.integrations'), path: 'integrations' },
    { name: t('nav.blog'), path: 'blog' },
    { name: t('nav.portfolio'), path: 'case-studies' },
    { name: t('nav.about'), path: 'about' },
    { name: t('nav.contact'), path: 'contact' }
  ];

  const getPath = (item) => {
    return `/${currentLang}${item.path ? '/' + item.path : ''}`;
  };

  return (
    <header className="bg-slate-900/95 text-white shadow-lg sticky top-0 z-50 backdrop-blur-md border-b border-gray-800">
      <div className="container mx-auto px-6 py-4 flex justify-between items-center">
        
        <Link to={`/${currentLang}`} className="-m-1.5 p-1.5" onClick={() => setIsMobileMenuOpen(false)}>
          <img src={Logo} alt="Mupax" width="160" height="40" className="h-10 w-auto" />
        </Link>
        
        <div className="flex items-center gap-4 md:gap-8">
          {/* DESKTOP NAV */}
          <nav className="hidden md:flex gap-8 text-sm font-medium">
            {navigation.map((item) => (
              <NavLink 
                key={item.path} 
                to={getPath(item)} 
                className={navLinkClasses}
                // PARANDUS: Kui path on tühi (Avaleht), nõuame täpset lõppu.
                // See väldib olukorda, kus Avaleht on aktiivne ka alamlehtedel.
                end={item.path === ''}
              >
                {item.name}
              </NavLink>
            ))}
          </nav>
          
          <button 
            onClick={toggleLang} 
            className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 px-3 py-1.5 md:px-4 md:py-2 rounded-lg transition group"
          >
            <LanguageIcon className="h-5 w-5 text-blue-400 group-hover:text-white transition" />
            <span className="uppercase text-xs md:text-sm font-bold tracking-wider">{currentLang}</span>
          </button>

          {/* MOBILE MENU BUTTON */}
          <button
            className="md:hidden p-2 text-gray-300 hover:text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle Mobile Menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav"
          >
            {isMobileMenuOpen ? <XMarkIcon className="h-7 w-7" /> : <Bars3Icon className="h-7 w-7" />}
          </button>
        </div>
      </div>

      {/* MOBILE MENU BACKDROP */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 top-[73px] bg-black/40 z-40"
          aria-hidden="true"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* MOBILE MENU DROPDOWN */}
      {isMobileMenuOpen && (
        <nav
          id="mobile-nav"
          role="navigation"
          aria-label={t('nav_aria')}
          className="md:hidden bg-slate-900 border-b border-gray-800 px-6 py-8 space-y-6 animate-fade-in relative z-50"
        >
          {navigation.map((item) => (
            <NavLink
              key={item.path}
              to={getPath(item)}
              className="block text-lg font-semibold text-gray-300 hover:text-blue-400"
              onClick={() => setIsMobileMenuOpen(false)}
              end={item.path === ''}
            >
              {item.name}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}
